import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronLeft } from "lucide-react";
import "@fontsource/noto-sans-devanagari/400.css";
import "@fontsource/noto-sans-devanagari/700.css";
import { PageHeader as Header } from "@/components/landing/PageHeader";

const EFFECTIVE_DATE = "1 January 2025";
const GRIEVANCE_EMAIL = "grievance@urgentsellbuy.com";
const COMPANY = "UrgentSellBuy";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | UrgentSellBuy";
    window.scrollTo(0, 0);
    return () => { document.title = "UrgentSellBuy | Sell it today. Buy it first."; };
  }, []);

  return (
    <>
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-24 [font-family:'Instrument_Sans','Noto_Sans_Devanagari',sans-serif]" data-testid="privacy-page">
        <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors mb-6">
          <ChevronLeft className="w-4 h-4" /> वापस जाएं
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#FF3838]/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#FF3838]" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white" data-testid="privacy-title">Privacy Policy</h1>
            <p className="text-xs text-zinc-400" data-testid="privacy-effective">Effective: {EFFECTIVE_DATE} · Version 2.0</p>
          </div>
        </div>

        <div className="bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-2xl p-4 mb-4 text-sm text-blue-200 space-y-2" data-testid="privacy-law-notice">
          <p>
            <strong>Applicable Data Protection Framework:</strong> आपके personal data की protection currently निम्नलिखित laws के तहत है:
          </p>
          <ul className="ml-4 space-y-0.5 text-xs">
            <li>• <strong>Information Technology Act, 2000 — Section 43A</strong> (body corporate data protection duty)</li>
            <li>• <strong>IT (SPDI) Rules, 2011</strong> — Sensitive Personal Data or Information Rules</li>
            <li>• <strong>IT Rules 2021</strong> — privacy commitments as per Rule 3(1)(a)</li>
          </ul>
          <p className="text-xs italic">
            Digital Personal Data Protection Act, 2023 (DPDP Act): Enacted 11 August 2023. As of these Terms' effective date, the Act's definitions and Data Protection Board institutional framework are operative (MeitY notification G.S.R. 843(E), 13 November 2025). The core obligations on Data Fiduciaries — consent architecture, Data Principal rights (access, correction, erasure, nomination), security safeguards, breach notification under the DPDP Act, and penalties — are scheduled to commence on <strong>13 May 2027</strong>. This Policy will be updated as those provisions come into force. Current obligations are under IT Act, 2000 and SPDI Rules, 2011.
          </p>
        </div>

        <div className="space-y-6 text-sm text-zinc-200 leading-relaxed">

          <Section title="1. Data Controller की पहचान">
            <p>
              <strong>{COMPANY}</strong> — IT Act Section 43A के meaning में एक <strong>body corporate</strong> — आपके personal data का controller है।
            </p>
            <p>आप Platform के user (data subject) हैं।</p>
          </Section>

          <Section title="2. हम कौन सा Personal Data collect करते हैं">
            <div className="space-y-2">
              {[
                {
                  label: "Account &amp; Identity Data",
                  detail: "नाम, email, mobile number, username, profile photo, date of birth (age verification)"
                },
                {
                  label: "Listing Data",
                  detail: "Title, description, images, price, state/city location (exact address नहीं)"
                },
                {
                  label: "Transaction &amp; Payment Data",
                  detail: "Payment order IDs, transaction status. Card/bank details हम store नहीं करते — Razorpay (PCI-DSS compliant, RBI-regulated) handle करता है।"
                },
                {
                  label: "Communication Data",
                  detail: "Platform के अंदर chat messages और contact requests"
                },
                {
                  label: "Technical Data",
                  detail: "IP address, device type, browser, session data, viewed listings (platform operations के लिए)"
                },
              ].map((item) => (
                <div key={item.label} className="bg-[#12141C] border border-white/[0.06] rounded-xl p-3">
                  <p className="font-semibold text-xs text-white" dangerouslySetInnerHTML={{ __html: item.label }} />
                  <p className="text-zinc-400 text-xs mt-0.5">{item.detail}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-400">
              IT (SPDI) Rules, 2011 के तहत <strong>Sensitive Personal Data or Information (SPDI)</strong> — passwords, financial information, health data आदि — को additional protection के साथ handle किया जाता है।
            </p>
          </Section>

          <Section title="3. Data Collection और Processing का Legal Basis">
            <ul>
              <li><strong>Consent:</strong> Registration के समय explicit consent — IT (SPDI) Rules 2011 के अनुसार</li>
              <li><strong>Contract Performance:</strong> Platform services provide करने के लिए necessary</li>
              <li><strong>Legal Obligation:</strong> Indian laws का पालन, valid court orders, competent authority requests</li>
              <li><strong>Legitimate Interest:</strong> Fraud prevention, platform safety, security (IT Act Section 43A के अनुसार)</li>
            </ul>
          </Section>

          <Section title="4. Data का Purpose">
            <p>हम आपका data केवल specified purposes के लिए use करते हैं:</p>
            <ul>
              <li>Platform services और features provide करना</li>
              <li>Buyer-seller communication facilitate करना</li>
              <li>Payment processing (Razorpay के माध्यम से)</li>
              <li>Fraud detection और platform safety (IT Act Section 43A duty)</li>
              <li>Valid legal requirements — court orders, competent authority requests</li>
              <li>Platform improvements (aggregated/anonymized data)</li>
              <li>Important service notifications</li>
            </ul>
            <p className="font-medium">हम आपका data third parties को sell नहीं करते।</p>
          </Section>

          <Section title="5. Data Sharing">
            <ul>
              <li><strong>Transaction Parties:</strong> जब आप contact initiate करते हैं, relevant contact details shared होते हैं।</li>
              <li><strong>Razorpay:</strong> Payment processing — RBI-regulated, PCI-DSS compliant; उनकी Privacy Policy applicable।</li>
              <li><strong>Law Enforcement / Competent Authorities:</strong> Valid court order, IT Act Section 69/69A order, या अन्य legally valid request पर। हम केवल legally required information share करते हैं।</li>
              <li><strong>Service Providers:</strong> Hosting, security — confidentiality obligations के तहत, केवल necessary data।</li>
              <li><strong>Business Transfer:</strong> Merger/acquisition — आपको advance notice दिया जाएगा।</li>
            </ul>
          </Section>

          <Section title="6. Data Security — IT Act Section 43A और SPDI Rules 2011">
            <p>IT Act Section 43A के तहत body corporates का duty है कि वे <strong>"reasonable security practices and procedures"</strong> implement करें। SPDI Rules 2011 के अनुसार:</p>
            <ul>
              <li>Data transmission encryption (HTTPS/TLS)</li>
              <li>Access controls और authentication</li>
              <li>Comprehensive documented information security programme/policies (Rule 8)</li>
              <li>Designated Data Protection Officer / responsible person</li>
            </ul>
            <p className="text-xs text-zinc-400 italic">
              DPDP Act 2023 के तहत Data Protection Board को breach notification और DPDP Act penalties: scheduled May 13, 2027 commencement। CERT-In Directions (April 2022) के तहत reporting obligations currently applicable।
            </p>
          </Section>

          <Section title="7. Data Retention">
            <ul>
              <li>Account active होने तक</li>
              <li>Legal obligation जब तक require करे (जैसे tax records — GST/IT Act requirement)</li>
              <li>Active legal dispute resolution तक</li>
            </ul>
            <p>Account deletion request के बाद, personal data reasonable time में delete किया जाएगा, जहाँ law retain करना require नहीं करती।</p>
          </Section>

          <Section title="8. आपके Rights — Current Applicable Law के तहत">
            <div className="space-y-2">
              {[
                {
                  right: "Access (IT Act, SPDI Rules 2011 Rule 5)",
                  desc: "आप हमसे request कर सकते हैं कि हम आपके stored personal data के बारे में जानकारी provide करें।"
                },
                {
                  right: "Correction",
                  desc: "Inaccurate या outdated personal data correct करने का request कर सकते हैं।"
                },
                {
                  right: "Consent Withdrawal",
                  desc: "SPDI Rules 2011 के तहत आप अपनी consent withdraw कर सकते हैं — platform services affected हो सकती हैं।"
                },
                {
                  right: "Grievance (IT Rules 2021 Rule 3(2)(a))",
                  desc: "Privacy-related complaints के लिए Grievance Officer से contact — 24h acknowledgment, 15 days resolution।"
                },
              ].map((r) => (
                <div key={r.right} className="bg-[#12141C] border border-white/[0.06] rounded-xl p-3">
                  <p className="font-semibold text-xs text-white">{r.right}</p>
                  <p className="text-zinc-400 text-xs mt-0.5">{r.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-400 italic">
              DPDP Act 2023 Chapter III rights (Access, Correction, Erasure, Nomination as Data Principal): scheduled for commencement 13 May 2027. Policy will be updated when those provisions come into force.
            </p>
            <p>Rights exercise करने के लिए: <a href={`mailto:${GRIEVANCE_EMAIL}`} data-testid="privacy-rights-email" className="text-[#FF3838] underline">{GRIEVANCE_EMAIL}</a></p>
          </Section>

          <Section title="9. Children का Data">
            <p>Platform <strong>18 वर्ष से कम</strong> आयु के व्यक्तियों के लिए नहीं है। यदि हमें किसी minor का data प्राप्त होता है, तो उसे delete किया जाएगा।</p>
            <p className="text-xs text-zinc-400 italic">DPDP Act 2023 Section 9 (verifiable parental consent for children): scheduled for 13 May 2027 commencement।</p>
          </Section>

          <Section title="10. Cookies">
            <p>Platform basic functional cookies use करता है (session management, security)। आप browser settings से non-essential cookies disable कर सकते हैं। Cookies Notice के लिए <Link to="/legal/cookies" data-testid="privacy-cookies-link" className="text-[#FF3838] underline">Cookies Policy</Link> देखें।</p>
          </Section>

          <Section title="11. Grievance Officer — Privacy Complaints">
            <div className="bg-[#12141C] border border-white/[0.06] rounded-xl p-4 space-y-1 text-sm">
              <p><strong>Email:</strong> <a href={`mailto:${GRIEVANCE_EMAIL}`} data-testid="privacy-grievance-email" className="text-[#FF3838] underline">{GRIEVANCE_EMAIL}</a></p>
              <p><strong>Acknowledgment:</strong> 24 घंटों के भीतर (IT Rules 2021 Rule 3(2)(a)(i))</p>
              <p><strong>Resolution:</strong> 15 दिनों के भीतर</p>
              <p className="text-xs text-zinc-400 mt-2">
                Company: {COMPANY}.<br />
                DPDP Act 2023 के तहत Data Protection Board of India: established pursuant to November 2025 commencement; its full adjudicatory functions will be operational once related Rules and substantive provisions commence.
              </p>
            </div>
          </Section>

        </div>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-base font-bold text-white mb-2 border-b border-white/10 pb-1">{title}</h2>
      <div className="space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:marker:text-[#FF3838]">{children}</div>
    </section>
  );
}
